/**
 * Usuários — quem entra no sistema e com quanta autoridade.
 *
 * Tela de dono. Até existir, contratar alguém dependia de acesso ao terminal
 * do servidor e demitir dependia de SQL na mão.
 *
 * A tela esconde o que o papel não alcança, mas quem recusa de verdade é a
 * API: botão escondido não é permissão, é cortesia.
 */
import { useCallback, useEffect, useState } from "react";
import {
  api, lerSessao, NOME_DO_PAPEL, PAPEIS,
  type Papel, type Usuario,
} from "../api.ts";
import { Campo, Selecao, Tabela } from "../componentes/Campos.tsx";

const OPCOES_PAPEL = PAPEIS.map((p) => [p, NOME_DO_PAPEL[p]] as const);

/** O que cada papel alcança, em uma linha. Aparece ao lado do seletor. */
const O_QUE_ALCANCA: Record<Papel, string> = {
  DONO: "Tudo, inclusive usuários e cancelamento de nota fiscal.",
  ESCRITORIO: "Comercial, fiscal, financeiro e cadastros. Não mexe em usuários.",
  PATIO: "Movimento de estoque, romaneio e consulta. Não vê o financeiro.",
};

function dataCurta(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

export function Usuarios({ aviso }: { aviso: (t: string) => void }) {
  const [lista, setLista] = useState<Usuario[] | null>(null);
  const euId = lerSessao()?.usuario.id ?? "";

  const recarregar = useCallback(() => {
    void api.get<Usuario[]>("/usuarios").then(setLista).catch(() => setLista([]));
  }, []);
  useEffect(recarregar, [recarregar]);

  const donosAtivos = (lista ?? []).filter((u) => u.papel === "DONO" && u.ativo).length;

  return (
    <>
      <div className="cab-doc">
        <h1>Usuários</h1>
      </div>

      <div className="rolagem">
        <NovoUsuario aviso={aviso} aoCriar={recarregar} />

        <Tabela
          vazio="Nenhum usuário — o que é impossível, porque você está logado."
          dados={lista}
          colunas={["Nome", "E-mail", "Papel", "Situação", "Senha desde", ""]}
          linha={(u: Usuario) => [
            <b key="n">{u.nome}{u.id === euId && (
              <span className="rotulo" style={{ marginLeft: 6 }}>você</span>
            )}</b>,
            <span key="e" className="mono">{u.email}</span>,
            <SeletorPapel
              key="p"
              usuario={u}
              // O último dono ativo não pode ser rebaixado — a API recusa, e o
              // seletor desabilitado explica antes de a pessoa tentar.
              travado={u.papel === "DONO" && donosAtivos <= 1}
              aviso={aviso}
              aoMudar={recarregar}
            />,
            u.ativo
              ? <span key="s" className="pil p-ok"><i />ativo</span>
              : <span key="s" className="pil p-erro"><i />desativado</span>,
            <span key="d" className="num">{dataCurta(u.senhaAlteradaEm)}</span>,
            <Acoes
              key="a"
              usuario={u}
              souEu={u.id === euId}
              ultimoDono={u.papel === "DONO" && donosAtivos <= 1}
              aviso={aviso}
              aoMudar={recarregar}
            />,
          ]}
        />

        <div className="painel">
          <div className="tt"><span className="rotulo">O que cada papel alcança</span></div>
          <div style={{ padding: 14, display: "grid", gap: 8, fontSize: 13 }}>
            {PAPEIS.map((p) => (
              <div key={p} style={{ display: "flex", gap: 10 }}>
                <b style={{ minWidth: 90 }}>{NOME_DO_PAPEL[p]}</b>
                <span style={{ color: "var(--texto-secundario)" }}>{O_QUE_ALCANCA[p]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------ novo usuário */

function NovoUsuario({
  aviso, aoCriar,
}: {
  aviso: (t: string) => void;
  aoCriar: () => void;
}) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [papel, setPapel] = useState<Papel>("PATIO");
  const [ocupado, setOcupado] = useState(false);

  const curta = senha.length > 0 && senha.length < 10;
  const pronto = nome.trim() !== "" && email.trim() !== "" && senha.length >= 10;

  async function salvar() {
    setOcupado(true);
    try {
      await api.post("/usuarios", { nome, email, senha, papel });
      aviso(`${nome} cadastrado como ${NOME_DO_PAPEL[papel].toLowerCase()}`);
      setNome(""); setEmail(""); setSenha(""); setPapel("PATIO");
      aoCriar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao cadastrar");
    } finally { setOcupado(false); }
  }

  return (
    <div className="painel">
      <div className="tt"><span className="rotulo">Novo usuário</span></div>
      <div style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Campo rotulo="Nome" largura={180} valor={nome} aoMudar={setNome} />
        <Campo rotulo="E-mail (é o login)" largura={230} valor={email} aoMudar={setEmail} />
        <label>
          <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>
            Senha inicial
          </span>
          <input
            className="campo-texto foco-claro"
            type="password"
            style={{
              width: 190,
              ...(curta ? { borderColor: "var(--erro-texto)", background: "var(--erro-fundo)" } : {}),
            }}
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
          />
        </label>
        <Selecao
          rotulo="Papel" largura={150} valor={papel}
          aoMudar={(v) => setPapel(v as Papel)} opcoes={OPCOES_PAPEL}
        />
        <button className="bt" onClick={() => void salvar()} disabled={ocupado || !pronto}>
          Cadastrar
        </button>
      </div>
      <div style={{ padding: "0 14px 12px", fontSize: 12, color: "var(--texto-terciario)" }}>
        {curta
          ? "A senha precisa de pelo menos 10 caracteres."
          : `${O_QUE_ALCANCA[papel]} A pessoa pode trocar a própria senha depois de entrar.`}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- linha */

function SeletorPapel({
  usuario, travado, aviso, aoMudar,
}: {
  usuario: Usuario; travado: boolean;
  aviso: (t: string) => void; aoMudar: () => void;
}) {
  const [ocupado, setOcupado] = useState(false);

  async function trocar(novo: string) {
    if (novo === usuario.papel) return;
    setOcupado(true);
    try {
      await api.patch(`/usuarios/${usuario.id}`, { papel: novo });
      aviso(`${usuario.nome} agora é ${NOME_DO_PAPEL[novo as Papel].toLowerCase()}`);
      aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao trocar o papel");
      aoMudar();
    } finally { setOcupado(false); }
  }

  return (
    <select
      className="campo-texto foco-claro"
      style={{ width: 130, height: 26, fontSize: 12 }}
      value={usuario.papel}
      disabled={ocupado || travado}
      title={travado ? "É o último dono ativo — promova outra pessoa antes" : undefined}
      onChange={(e) => void trocar(e.target.value)}
    >
      {OPCOES_PAPEL.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
    </select>
  );
}

function Acoes({
  usuario, souEu, ultimoDono, aviso, aoMudar,
}: {
  usuario: Usuario; souEu: boolean; ultimoDono: boolean;
  aviso: (t: string) => void; aoMudar: () => void;
}) {
  const [redefinindo, setRedefinindo] = useState(false);
  const [senha, setSenha] = useState("");
  const [ocupado, setOcupado] = useState(false);

  async function alternarAtivo() {
    setOcupado(true);
    try {
      await api.patch(`/usuarios/${usuario.id}`, { ativo: !usuario.ativo });
      aviso(
        usuario.ativo
          ? `${usuario.nome} desativado — as sessões abertas caíram`
          : `${usuario.nome} reativado`,
      );
      aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao alterar");
    } finally { setOcupado(false); }
  }

  async function redefinir() {
    setOcupado(true);
    try {
      await api.post(`/usuarios/${usuario.id}/senha`, { senha });
      aviso(`Senha de ${usuario.nome} redefinida — as sessões dele caíram`);
      setSenha(""); setRedefinindo(false);
      aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao redefinir");
    } finally { setOcupado(false); }
  }

  if (redefinindo) {
    return (
      <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          className="campo-texto foco-claro"
          type="password" autoFocus placeholder="nova senha"
          style={{ width: 150, height: 26, fontSize: 12 }}
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && senha.length >= 10) void redefinir();
            if (e.key === "Escape") { setSenha(""); setRedefinindo(false); }
          }}
        />
        <button
          className="bt" style={{ padding: "3px 8px", fontSize: 12 }}
          disabled={ocupado || senha.length < 10}
          onClick={() => void redefinir()}
        >
          Gravar
        </button>
        <button
          className="bt bt-neutro" style={{ padding: "3px 8px", fontSize: 12 }}
          onClick={() => { setSenha(""); setRedefinindo(false); }}
        >
          Esc
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: "flex", gap: 6 }}>
      <button
        className="bt bt-neutro" style={{ padding: "3px 8px", fontSize: 12 }}
        disabled={ocupado}
        onClick={() => setRedefinindo(true)}
      >
        Redefinir senha
      </button>
      <button
        className="bt bt-neutro" style={{ padding: "3px 8px", fontSize: 12 }}
        disabled={ocupado || souEu || (usuario.ativo && ultimoDono)}
        title={
          souEu
            ? "Você não pode desativar o próprio acesso"
            : usuario.ativo && ultimoDono
              ? "É o último dono ativo — promova outra pessoa antes"
              : undefined
        }
        onClick={() => void alternarAtivo()}
      >
        {usuario.ativo ? "Desativar" : "Reativar"}
      </button>
    </span>
  );
}

/* --------------------------------------------------------- própria senha */

/**
 * Troca da própria senha. Disponível a todos os papéis, e é de propósito:
 * quem não pode trocar a própria senha acaba com ela anotada no monitor.
 */
export function TrocarSenha({
  aviso, aoFechar,
}: {
  aviso: (t: string) => void;
  aoFechar: () => void;
}) {
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [repetida, setRepetida] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const curta = nova.length > 0 && nova.length < 10;
  const diferem = repetida.length > 0 && nova !== repetida;
  const pronto = atual !== "" && nova.length >= 10 && nova === repetida;

  async function gravar() {
    setOcupado(true);
    try {
      await api.trocarSenha(atual, nova);
      aviso("Senha trocada. As sessões nos outros aparelhos foram encerradas.");
      aoFechar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao trocar a senha");
    } finally { setOcupado(false); }
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        background: "rgba(0,0,0,.45)",
        display: "grid", placeItems: "center",
      }}
      onClick={aoFechar}
    >
      <div
        className="painel"
        style={{ width: 340, margin: 0 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === "Escape") aoFechar(); }}
      >
        <div className="tt"><span className="rotulo">Trocar minha senha</span></div>
        <div style={{ padding: 14, display: "grid", gap: 10 }}>
          <Senha rotulo="Senha atual" valor={atual} aoMudar={setAtual} foco />
          <Senha rotulo="Nova senha" valor={nova} aoMudar={setNova} invalido={curta} />
          <Senha rotulo="Repita a nova" valor={repetida} aoMudar={setRepetida} invalido={diferem} />

          <div style={{ fontSize: 12, color: "var(--texto-terciario)" }}>
            {curta
              ? "Pelo menos 10 caracteres."
              : diferem
                ? "As duas não conferem."
                : "Os outros aparelhos onde você entrou serão desconectados."}
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="bt bt-neutro" onClick={aoFechar}>Cancelar</button>
            <button className="bt" disabled={ocupado || !pronto} onClick={() => void gravar()}>
              Trocar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Senha({
  rotulo, valor, aoMudar, invalido, foco,
}: {
  rotulo: string; valor: string; aoMudar: (v: string) => void;
  invalido?: boolean; foco?: boolean;
}) {
  return (
    <label>
      <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>{rotulo}</span>
      <input
        className="campo-texto foco-claro"
        type="password"
        autoFocus={foco}
        style={{
          width: "100%",
          ...(invalido ? { borderColor: "var(--erro-texto)", background: "var(--erro-fundo)" } : {}),
        }}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
      />
    </label>
  );
}
